import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloneBroadcast } from "../../pages";
import * as yup from "yup";
import constant from "../../utils/constant";
import { MimaToastUtil } from "../../components";
import { BroadcastController } from "../../controllers";
import { toJS } from "mobx";
import { adminStore } from "../../stores";
import { useFileUpload } from "use-file-upload";
import { handleImageUpload } from "../../utils/utils";

const CloneBroadcastContainer = ({ closeModal, data }) => {
  const [loading, setLoading] = useState(false);
  const [competition, setCompetition] = useState("");
  const [stage, setStage] = useState("");
  const [file, selectFile] = useFileUpload();

  const selectClick = () => {
    selectFile({ accept: "image/*" });
  };

  const broadcast = adminStore.singleBroadcast;

  const navigate = useNavigate();

  const goToBroadcastPage = () => {
    adminStore.setSingleBroadcast({});
    navigate(constant.Routes.Broadcast);
  };
  const query = useMemo(() => {
    return `stage=${stage}&competition=${competition}`;
  }, [stage, competition]);

  useEffect(() => {
    const fetchPlan = async () => {
      setLoading(true);
      await Promise.all([
        BroadcastController.getMimaPlans(),
        BroadcastController.getFashPlans(),
        BroadcastController.getTags(),
        BroadcastController.getAllBusinesses(),
        BroadcastController.getCompetitionParticipants(query),
      ]);

      setLoading(false);
    };

    fetchPlan();
  }, [query]);

  const validationSchema = yup.object().shape({
    title: yup.string().when("channels", {
      is: (channels) =>
        channels && (channels.includes("EMAIL") || channels.includes("PUSH")),
      then: yup.string().required("Broadcast Title is required"),
      otherwise: yup.string().nullable(),
    }),
    content: yup.string().when("channels", {
      is: (channels) => channels && channels.includes("EMAIL"),
      then: yup.string().required("Content is required"),
      otherwise: yup.string().nullable(),
    }),
    summary: yup.string().when("channels", {
      is: (channels) =>
        channels && (channels.includes("SMS") || channels.includes("PUSH")),
      then: yup
        .string()
        .max(
          159,
          "Summary length must be less than or equal to 159 characters long"
        )
        .required("Broadcast Summary is required"),
      otherwise: yup.string().nullable(),
    }),
    startDate: yup.date().when("filterType", {
      is: "DATE",
      then: yup.date().required("Start date is required").nullable(),
      otherwise: yup.date().nullable(),
    }),
    endDate: yup.date().when("filterType", {
      is: "DATE",
      then: yup.date().required("End date is required").nullable(),
      otherwise: yup.date().nullable(),
    }),
    dateType: yup.string().when("filterType", {
      is: "DATE",
      then: yup.string().required("Date Type is required").nullable(),
      otherwise: yup.string().nullable(),
    }),
    filterType: yup.string().required("Filter Type is required"),
    planName: yup.string().when(["filterType", "isTrial"], {
      is: (filterType, isTrial) => filterType === "PLAN" && isTrial === false,
      then: yup.string().required("Plan is required").nullable(),
      otherwise: yup.string().nullable(),
    }),
    buttonTitle: yup.string().nullable(),
    buttonUrl: yup.string().url("Invalid URL").nullable(),
    expectedDeliveryDateTime: yup.date().nullable(),

    businesses: yup.array().when(["filterType", "broadCastFor"], {
      is: (filterType, broadCastFor) =>
        filterType === "DIRECT" && broadCastFor === "CUSTOMERS",
      then: yup
        .array()
        .of(yup.string().required("Business name is required"))
        .min(1, "Select at least one business"),
      otherwise: yup.array().of(yup.string().nullable()).nullable(),
    }),
    prospects: yup.array().when(["filterType", "broadCastFor"], {
      is: (filterType, broadCastFor) =>
        filterType === "DIRECT" && broadCastFor === "PROSPECTS",
      then: yup
        .array()
        .of(yup.string().required("Prospect name is required"))
        .min(1, "Select at least one prospect"),
      otherwise: yup.array().of(yup.string().nullable()).nullable(),
    }),
    participants: yup.array().when(["filterType", "broadCastFor"], {
      is: (filterType, broadCastFor) =>
        filterType === "DIRECT" && broadCastFor === "COMPETITION",
      then: yup
        .array()
        .of(yup.string().required("Participant name is required"))
        .min(1, "Select at least one Participant"),
      otherwise: yup.array().of(yup.string().nullable()).nullable(),
    }),

    channels: yup
      .array()
      .of(yup.string().required())
      .min(1, "Select at least one broadcast channel"),
    app: yup
      .string()
      .required("App is required")
      .oneOf(["FASH", "MIMA", "MONEYEX"], "Select App for broadcast"),
  });

  const onSubmit = async (values) => {
    if (values.hasBanner && (!values.files || values.files.length < 1)) {
      return MimaToastUtil.error({
        message: "Kindly add at least 1 image",
      });
    }
    setLoading(true);

    let imageUrls = [];
    if (values.hasBanner) {
      const file = values.files?.[0];
      imageUrls = [file.preview];
      if (file.name !== file.preview) {
        imageUrls = await handleImageUpload(values.files);
      }
    }

    const queryParamsArray = [
      `app=${encodeURIComponent(values.app)}`,
      `filterType=${encodeURIComponent(values.filterType)}`,
      `broadCastFor=${encodeURIComponent(values.broadCastFor)}`,
    ];

    if (values.filterType === "COMPETITION") {
      queryParamsArray.push(
        `competition=${encodeURIComponent(values.competitions)}`
      );
    }
    if (values.plan) {
      queryParamsArray.push(`plan=${encodeURIComponent(values.plan)}`);
    }
    if (values.subType) {
      queryParamsArray.push(`subType=${encodeURIComponent(values.subType)}`);
    }
    if (values.prospectTag) {
      queryParamsArray.push(
        `prospectTag=${encodeURIComponent(values.prospectTag)}`
      );
    }

    if (values.dateType) {
      queryParamsArray.push(`dateType=${encodeURIComponent(values.dateType)}`);
    }
    if (values.startDate) {
      queryParamsArray.push(
        `startDate=${encodeURIComponent(values.startDate)}`
      );
    }
    if (values.endDate) {
      queryParamsArray.push(`endDate=${encodeURIComponent(values.endDate)}`);
    }
    const queryParams = queryParamsArray.join("&");

    const payload = {
      businesses: values.businesses,
      participants: values.participants,
      channels: values.channels,
      ...(values.broadCastFor === "PROSPECTS"
        ? { prospects: values.prospects }
        : []),
      ...(values.channels.includes("EMAIL") || values.channels.includes("PUSH")
        ? { title: values.title }
        : {}),
      ...(values.channels.includes("SMS") || values.channels.includes("PUSH")
        ? { summary: values.summary }
        : {}),
      ...(values.channels.includes("EMAIL")
        ? {
            content: values.content,
            ...(values.hasBanner ? { bannerUrl: imageUrls[0] || "" } : ""),
          }
        : {}),
      ...(values.buttonTitle || values.buttonUrl
        ? {
            actionButton: {
              title: values.buttonTitle,
              url: values.buttonUrl,
            },
          }
        : {}),
      ...(values.buttonTitle2 || values.buttonUrl2
        ? {
            actionButton2: {
              title2: values.buttonTitle2,
              url2: values.buttonUrl2,
            },
          }
        : {}),
      ...(values.expectedDeliveryDateTime
        ? { expectedDeliveryDateTime: values.expectedDeliveryDateTime }
        : {}),
    };

    const { status, errorMessage } = await BroadcastController.createBroadcast(
      payload,
      queryParams
    );
    if (status === constant.Success) {
      MimaToastUtil.success({ message: constant.Success });
      setLoading(false);
      return goToBroadcastPage();
    } else {
      setLoading(false);
      MimaToastUtil.error({ message: errorMessage });
    }
  };

  return (
    <CloneBroadcast
      validationSchema={validationSchema}
      loading={loading}
      closeModal={closeModal}
      data={data}
      onSubmit={onSubmit}
      fashPlans={toJS(adminStore.fashPlans)}
      mimaPlans={toJS(adminStore.mimaPlans)}
      broadcast={broadcast}
      selectClick={selectClick}
      tags={toJS(adminStore.tags)}
      file={file}
      setCompetition={setCompetition}
      setStage={setStage}
    />
  );
};
export default CloneBroadcastContainer;
